import { useMutation, useQueryClient } from "@tanstack/react-query";
import useApiHelper from "../hooks/useApiHelper";
import { DefaultResponseWithData } from "../models/system";
import { IInvoice } from "../models/invoice/IInvoice";
import { useAuth } from "../contexts/AuthContext";
import { apiImportRequestTimeoutMs, resourceAPIURL } from "../config";
import { IInvoiceItem } from "../models/Invoice";
import { fileNameFromContentDisposition } from "../utils/contentDisposition";

export type DownloadInvoicesRequest = {
  ids?: string[];
  filter?: Record<string, unknown>;
  query?: string;
};

function triggerBlobDownload(blob: Blob, fileName: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}


async function refusalMessage(response: Response, fallback: string) {
  const message = await response
    .clone()
    .json()
    .then((body) => body?.message)
    .catch(() => undefined);

  return message || fallback;
}


export default function useInvoiceMutations() {
  const { get, post, put, del } = useApiHelper();
  const { postRaw } = useApiHelper({ timeoutMs: apiImportRequestTimeoutMs });
  const queryClient = useQueryClient();
  const { claims, token } = useAuth();

  const createItem = useMutation({
    mutationFn: (payload: any) =>
      post<DefaultResponseWithData<IInvoice>>(`/invoice/${payload.invoiceId}/item`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      queryClient.invalidateQueries({ queryKey: ["pagedTable"] });
    },
  });

  // POST: Create Invoice
  const createInvoice = useMutation({
    mutationFn: (payload: any) =>
      post<DefaultResponseWithData<IInvoice>>("/invoice", payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["pagedTable"] });
    },
  });

  // PUT: Update Invoice
  const updateInvoice = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) =>
      put<DefaultResponseWithData<IInvoice>>(`/invoice/${id}`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      queryClient.invalidateQueries({ queryKey: ["pagedTable"] });
    },
  });

  const updateInvoiceItem = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) =>
      put<DefaultResponseWithData<IInvoiceItem>>(`/invoice/${id}/item/${payload.id}`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      queryClient.invalidateQueries({ queryKey: ["pagedTable"] });
    }
  });

  // DELETE: Delete Invoice
  const deleteInvoice = useMutation({
    mutationFn: (id: string) =>
      del<DefaultResponseWithData<boolean>>(`/invoice/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["pagedTable"] });
    },
  });

  const downloadInvoice = async (invoiceId: string) => {
    try {
      const response = await fetch(`${resourceAPIURL}/api/invoice/${invoiceId}/download`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          Authorization: `Bearer ${token}`
        },
      });

      if (!response.ok) {
        throw new Error("Failed to download invoice.");
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition");
      const fileName = fileNameFromContentDisposition(
        contentDisposition,
        `invoice_${invoiceId}.pdf`
      );

      triggerBlobDownload(blob, fileName);
    } catch (error) {
      console.error("Download error:", error);
      throw error;
    }
  };

  const downloadBulkInvoices = async (invoiceIds: string[], service: string) => {
    try {
      const response = await fetch(`${resourceAPIURL}/api/invoice/export/${service}/bulk`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ ids: invoiceIds })
      });

      if (!response.ok) {
        throw new Error(await refusalMessage(response, "Failed to download invoices."));
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition");
      const currentTimestamp = new Date().toISOString().replace(/[:.-]/g, "");
      const extension = service === "oracle" ? "xlsx" : "csv";
      const fileName = fileNameFromContentDisposition(
        contentDisposition,
        `invoices_${currentTimestamp}.${extension}`
      );

      triggerBlobDownload(blob, fileName);
    } catch (error) {
      console.error("Download error:", error);
      throw error;
    }
  }

  /**
   * Resolves to the number of invoices the API could not render. The zip still downloads —
   * one broken invoice should not cost you the other 200 — but a short zip is indistinguishable
   * from a complete one, so the caller warns when this is above zero.
   */
  const downloadInvoicePdfs = async (request: DownloadInvoicesRequest): Promise<number> => {
    try {
      const response = await postRaw("/invoice/download/bulk", {
        ids: request.ids,
        filter: request.filter,
        query: request.query,
      });

      if (!response.ok) {
        throw new Error(await refusalMessage(response, "Failed to download invoices."));
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition");
      const currentTimestamp = new Date().toISOString().replace(/[:.-]/g, "");
      const fileName = fileNameFromContentDisposition(
        contentDisposition,
        `invoices_${currentTimestamp}.zip`
      );

      triggerBlobDownload(blob, fileName);

      return Number(response.headers.get("X-Invoice-Pdf-Failures")) || 0;
    } catch (error) {
      console.error("Download error:", error);
      throw error;
    }
  };

  return {
    createInvoice,
    updateInvoice,
    deleteInvoice,
    downloadInvoice,
    downloadBulkInvoices,
    downloadInvoicePdfs,

    createItem,

    updateInvoiceItem
  };
}
